import { dashboardKeys, nearbyFeedKeys } from '@/features/events/feed/feedQuery';
import {
  patchFeedSavedDeal,
  patchVenueDetailSavedDeal,
  toggleSavedVenueDealRequest,
} from './savedVenueDealHelpers';
import { venueDetailKeys } from '@/features/venues/useVenueDetail';
import type { FeedResponse, VenueDetail, VenuePublicDeal } from '@/lib/api/apiTypes';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

type SavedVenueDealSnapshot = {
  feed: [readonly unknown[], InfiniteData<FeedResponse, number> | undefined][];
  venue: VenueDetail | undefined;
  venueID: string;
};

export { toggleSavedVenueDealRequest } from './savedVenueDealHelpers';

export async function applySavedVenueDealOptimisticUpdate(
  queryClient: QueryClient,
  {
    deal,
    venueID = deal.venue_id,
  }: {
    deal: VenuePublicDeal;
    venueID?: string;
  }
): Promise<SavedVenueDealSnapshot> {
  const venueQueryKey = venueDetailKeys.detail(venueID);
  const isSaved = !deal.is_saved;

  await Promise.all([
    queryClient.cancelQueries({ queryKey: nearbyFeedKeys.all }),
    queryClient.cancelQueries({ queryKey: venueQueryKey }),
  ]);

  // Saved deals appear in feed cards and venue detail; snapshot both so rollback stays consistent.
  const snapshot = {
    feed: queryClient.getQueriesData<InfiniteData<FeedResponse, number>>({
      queryKey: nearbyFeedKeys.all,
    }),
    venue: queryClient.getQueryData<VenueDetail>(venueQueryKey),
    venueID,
  };

  queryClient.setQueriesData<InfiniteData<FeedResponse, number>>(
    { queryKey: nearbyFeedKeys.all },
    (data) => (data ? patchFeedSavedDeal(data, deal.id, isSaved) : data)
  );
  queryClient.setQueryData<VenueDetail>(venueQueryKey, (detail) =>
    detail ? patchVenueDetailSavedDeal(detail, deal.id, isSaved) : detail
  );

  return snapshot;
}

export function restoreSavedVenueDealSnapshot(
  queryClient: QueryClient,
  snapshot?: SavedVenueDealSnapshot
) {
  if (!snapshot) {
    return;
  }
  for (const [queryKey, data] of snapshot.feed) {
    queryClient.setQueryData(queryKey, data);
  }
  queryClient.setQueryData(venueDetailKeys.detail(snapshot.venueID), snapshot.venue);
}

export async function invalidateSavedVenueDealQueries(queryClient: QueryClient, venueID: string) {
  // Dashboard counts are not patched optimistically, so they refetch with the canonical server state.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: nearbyFeedKeys.all }),
    queryClient.invalidateQueries({ queryKey: venueDetailKeys.detail(venueID) }),
    queryClient.invalidateQueries({ queryKey: dashboardKeys.current() }),
  ]);
}
